import React from 'react';
import { observer } from 'mobx-react';
import { FormikHelpers } from 'formik';
import {
    AppPage,
    AppHeader,
    AppContent,
    AppSectionHeader,
    ActionList,
    Button,
    ButtonPrimary,
    Form,
    InputSwitch,
    AppFooter,
    useSnackbar
} from '../../components-v2/shared';
import { InputText, useAsyncEffect } from '@lambdacurry/component-library';
import useStore from '../../store/useStore';
import { Company, DefaultMeetEliseModules, MeetEliseConfig } from '../../types';
import { unsavedChangesContainerSelectorDefault } from '../../constants';
import { omit } from 'lodash';
import * as Yup from 'yup';

const companyOmittedValues = [
    'active_coupon_id',
    'custom_disclaimer',
    'deleted_at',
    'estimate_multiplier',
    'widget_logo',
    'rev_link_google_date',
    'rev_link_google_small',
    'rev_link_yelp_date',
    'rev_link_yelp_small',
    'full_name'
];

interface MeetEliseConfigFormValues {
    meet_elise_config: MeetEliseConfig;
}

export const MeetEliseWidget = observer(() => {
    const { addSnackbar } = useSnackbar();
    const { store } = useStore();
    const { router, fetchCompanies, companies } = store;
    const { companyId } = router.params;
    const company = companies.getItem(parseInt(companyId, 10));

    const fetchRequiredData = async () => {
        await fetchCompanies();
    };
    useAsyncEffect(fetchRequiredData);

    const validationSchema = Yup.object().shape({
        meet_elise_config: Yup.object().nullable()
    });

    const formatDataToSave = (values: MeetEliseConfigFormValues) => {
        const companyValues = omit(company, companyOmittedValues);
        const { meet_elise_config: meet_elise_config_from_form, ...formValues } = values;

        return {
            ...companyValues,
            ...formValues,

            // Since we are using the `companies` API, we are essentially doing a patch here.
            // The API expects the `logo` field to be a base64 encoded data URL or `null`, so we are setting it to `null` to
            // avoid the "Input buffer contains unsupported image format" API error.
            // This is basically what is happening when you save the main company settings without adding/updating the logo image.
            logo: null,
            meet_elise_config: {
                ...meet_elise_config_from_form,
                enabled: company?.meet_elise_config?.enabled ?? false,
                modules: {
                    ...DefaultMeetEliseModules,
                    ...(meet_elise_config_from_form.modules || {})
                }
            }
        };
    };

    const handleSubmit = async (
        values: MeetEliseConfigFormValues,
        actions: FormikHelpers<MeetEliseConfigFormValues>
    ) => {
        const data = formatDataToSave(values);

        try {
            await companies.update(data);
            addSnackbar('MeetElise widget config has been updated.', { variant: 'success' });
            actions.resetForm({ values: formatCompanyForForm(data as Company) });
        } catch (error) {
            addSnackbar('Failed to update MeetElise widget config.', { variant: 'error' });
            actions.setErrors(error.response.data);
        }
    };

    const handleToggleClick = async () => {
        if (!company) {
            return;
        }

        const data = omit(company, companyOmittedValues);

        const newConfig: MeetEliseConfig = {
            ...(data.meet_elise_config || false),
            modules: data.meet_elise_config?.modules || DefaultMeetEliseModules,
            enabled: !data.meet_elise_config?.enabled
        };

        try {
            await companies.update({
                ...data,
                meet_elise_config: newConfig
            });
            addSnackbar(
                `MeetElise widget integration has been ${
                    data.meet_elise_config?.enabled ? 'deactivated' : 'activated'
                }.`,
                {
                    variant: 'success'
                }
            );
        } catch (error) {
            console.error(error.response.data);
            addSnackbar(
                `Failed to ${
                    data.meet_elise_config?.enabled ? 'deactivate' : 'activate'
                } the MeetElise widget integration.`,
                {
                    variant: 'error'
                }
            );
        }
    };

    const formatCompanyForForm = (company: Company): MeetEliseConfigFormValues => ({
        meet_elise_config: {
            enabled: company.meet_elise_config?.enabled ?? false,
            modules: company.meet_elise_config?.modules ?? DefaultMeetEliseModules
        }
    });

    const initialValues = (): Partial<Company> | any => (company ? formatCompanyForForm(company) : {});

    return (
        <AppPage loading={!company}>
            <AppHeader title="MeetElise Widget Configuration" icon={{ name: 'bellRing', color: 'blue' }}>
                <ActionList position="end">
                    <InputSwitch
                        labelOn="Active"
                        labelOff="Inactive"
                        labelPlacement="start"
                        onClick={handleToggleClick}
                        checked={company?.meet_elise_config?.enabled || false}
                    />
                </ActionList>
            </AppHeader>
            <div style={{ padding: '24px' }}>
                This tells the TCC application to move the MeetElise launcher buttons into itself. When this module is
                active, the original MeetElise launcher will be hidden. The corresponding modules in the TCC application
                will launch MeetElise instead.
            </div>

            {!!company && (
                <Form
                    validationSchema={validationSchema}
                    initialValues={initialValues()}
                    onSubmit={handleSubmit}
                    confirmUnsavedChanges
                    unsavedChangesConfig={{
                        containerQuerySelectorAll: unsavedChangesContainerSelectorDefault
                    }}
                >
                    {formikProps => (
                        <>
                            <AppContent className="field-group-content">
                                <div className="field-group-col">
                                    <AppSectionHeader title="Chat" />
                                    <div className="field-group-content">
                                        <InputSwitch
                                            labelOn="Active"
                                            labelOff="Inactive"
                                            labelPlacement="start"
                                            name="meet_elise_config.modules.chat.enabled"
                                            formikProps={formikProps}
                                            checked={
                                                formikProps.values.meet_elise_config?.modules?.chat?.enabled || false
                                            }
                                        />
                                        <InputText
                                            name="meet_elise_config.modules.chat.title"
                                            label="Chat Button Title"
                                            labelPlacement="inset"
                                            placeholder={
                                                company?.meet_elise_config?.modules?.chat?.title ||
                                                DefaultMeetEliseModules.chat?.title
                                            }
                                            disabled={!formikProps.values.meet_elise_config?.modules?.chat?.enabled}
                                            formikProps={formikProps}
                                        />
                                    </div>
                                    <AppSectionHeader title="Tour" />
                                    <div className="field-group-content">
                                        <InputSwitch
                                            labelOn="Active"
                                            labelOff="Inactive"
                                            labelPlacement="start"
                                            name="meet_elise_config.modules.tour.enabled"
                                            formikProps={formikProps}
                                            checked={
                                                formikProps.values.meet_elise_config?.modules?.tour?.enabled || false
                                            }
                                        />
                                        <InputText
                                            name="meet_elise_config.modules.tour.title"
                                            label="Tour Button Title"
                                            labelPlacement="inset"
                                            placeholder={
                                                company?.meet_elise_config?.modules?.tour?.title ||
                                                DefaultMeetEliseModules.tour?.title
                                            }
                                            disabled={!formikProps.values.meet_elise_config?.modules?.tour?.enabled}
                                            formikProps={formikProps}
                                        />
                                    </div>
                                    <AppSectionHeader title="Email" />
                                    <div className="field-group-content">
                                        <InputSwitch
                                            labelOn="Active"
                                            labelOff="Inactive"
                                            labelPlacement="start"
                                            name="meet_elise_config.modules.email.enabled"
                                            formikProps={formikProps}
                                            checked={
                                                formikProps.values.meet_elise_config?.modules?.email?.enabled || false
                                            }
                                        />
                                        <InputText
                                            name="meet_elise_config.modules.email.title"
                                            label="Email Button Title"
                                            labelPlacement="inset"
                                            placeholder={
                                                company?.meet_elise_config?.modules?.email?.title ||
                                                DefaultMeetEliseModules.email?.title
                                            }
                                            disabled={!formikProps.values.meet_elise_config?.modules?.email?.enabled}
                                            formikProps={formikProps}
                                        />
                                    </div>
                                    <AppSectionHeader title="Text" />
                                    <div className="field-group-content">
                                        <InputSwitch
                                            labelOn="Active"
                                            labelOff="Inactive"
                                            labelPlacement="start"
                                            name="meet_elise_config.modules.text.enabled"
                                            formikProps={formikProps}
                                            checked={
                                                formikProps.values.meet_elise_config?.modules?.text?.enabled || false
                                            }
                                        />
                                        <InputText
                                            name="meet_elise_config.modules.text.title"
                                            label="Text/Call Button Title"
                                            labelPlacement="inset"
                                            placeholder={
                                                company?.meet_elise_config?.modules?.text?.title ||
                                                DefaultMeetEliseModules.text?.title
                                            }
                                            disabled={!formikProps.values.meet_elise_config?.modules?.text?.enabled}
                                            formikProps={formikProps}
                                        />
                                    </div>
                                </div>
                            </AppContent>

                            <AppFooter>
                                <ActionList position="end">
                                    {formikProps.dirty && (
                                        <Button
                                            onClick={() => formikProps.resetForm()}
                                            disabled={formikProps.isSubmitting}
                                            data-lc-trigger-unsaved-changes={true}
                                            data-test-id="button_cancel"
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                    <ButtonPrimary
                                        type="submit"
                                        disabled={!formikProps.dirty || formikProps.isSubmitting}
                                        data-test-id="button_save"
                                    >
                                        Save
                                    </ButtonPrimary>
                                </ActionList>
                            </AppFooter>
                        </>
                    )}
                </Form>
            )}
        </AppPage>
    );
});
